import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

/**
 * App-wide toast notifications.
 *
 * Before this, UI commit calls (save a coaching note / clip /
 * observation, delete a clip, update settings, …) gave the coach no
 * confirmation — a successful save just blanked the form, and a
 * failure surfaced only as an easy-to-miss inline message (or
 * nothing). `useToast` gives every mutation a consistent, glanceable
 * success / error signal.
 *
 * Design:
 *   - Headless context here; the visual layer is `<ToastViewport>`.
 *   - `toast.success / error / info` push a toast; each auto-dismisses
 *     after `DEFAULT_TTL_MS` (errors linger longer — see `ttlFor`).
 *   - The provider mounts once at the app root (`main.tsx`); the
 *     viewport portals to `document.body` so toasts float above every
 *     surface including the Coach Review composer drawer + focus mode.
 *
 * No new dependencies, no new design tokens — the viewport styles
 * with the existing `good` / `flag` / `destructive` / `rule` tokens.
 */

export type ToastTone = "success" | "error" | "info";

export type Toast = {
  id: string;
  tone: ToastTone;
  /** Short headline — e.g. "Note saved". */
  message: string;
  /** Optional second line — e.g. an error detail. */
  description?: string;
};

/** Options accepted alongside the message. */
export type ToastOptions = {
  description?: string;
  /** Override the auto-dismiss delay (ms). `0` ⇒ never auto-dismiss. */
  ttlMs?: number;
};

export type ToastApi = {
  success: (message: string, opts?: ToastOptions) => string;
  error: (message: string, opts?: ToastOptions) => string;
  info: (message: string, opts?: ToastOptions) => string;
  /** Dismiss one toast early (e.g. the close button). */
  dismiss: (id: string) => void;
};

/** Success / info clear quickly; an error stays longer so the coach
 * has time to read what went wrong. `0` from the caller disables
 * auto-dismiss entirely. */
const DEFAULT_TTL_MS = 4000;
const ERROR_TTL_MS = 7000;

function ttlFor(tone: ToastTone, override?: number): number {
  if (override !== undefined) return override;
  return tone === "error" ? ERROR_TTL_MS : DEFAULT_TTL_MS;
}

type ToastContextValue = {
  toasts: Toast[];
  api: ToastApi;
};

const ToastContext = createContext<ToastContextValue | null>(null);

let counter = 0;
function nextId(): string {
  counter += 1;
  return `toast-${counter}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const active = useRef(true);
  // Deadlines survive StrictMode effect replay; timer handles belong
  // only to the currently mounted provider lifetime.
  const timers = useRef(new Map<string, {
    handle: ReturnType<typeof setTimeout> | null;
    expiresAt: number;
  }>());

  const dismiss = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) {
      if (timer.handle !== null) clearTimeout(timer.handle);
      timers.current.delete(id);
    }
    if (active.current) setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  useLayoutEffect(() => {
    active.current = true;
    const pending = timers.current;
    for (const [id, timer] of pending) {
      if (timer.handle === null) {
        timer.handle = setTimeout(() => dismiss(id), Math.max(0, timer.expiresAt - Date.now()));
      }
    }
    return () => {
      active.current = false;
      for (const timer of pending.values()) {
        if (timer.handle !== null) clearTimeout(timer.handle);
        timer.handle = null;
      }
    };
  }, [dismiss]);

  const push = useCallback(
    (tone: ToastTone, message: string, opts?: ToastOptions): string => {
      const id = nextId();
      if (!active.current) return id;
      setToasts((prev) => [
        ...prev,
        { id, tone, message, description: opts?.description },
      ]);
      const ttl = ttlFor(tone, opts?.ttlMs);
      if (ttl > 0) {
        timers.current.set(id, {
          handle: setTimeout(() => dismiss(id), ttl),
          expiresAt: Date.now() + ttl,
        });
      }
      return id;
    },
    [dismiss],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message, opts) => push("success", message, opts),
      error: (message, opts) => push("error", message, opts),
      info: (message, opts) => push("info", message, opts),
      dismiss,
    }),
    [push, dismiss],
  );

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, api }),
    [toasts, api],
  );

  return (
    <ToastContext.Provider value={value}>{children}</ToastContext.Provider>
  );
}

/** Read the toast list — for the viewport renderer only. */
export function useToastList(): { toasts: Toast[]; dismiss: ToastApi["dismiss"] } {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToastList must be used within <ToastProvider>");
  }
  return { toasts: ctx.toasts, dismiss: ctx.api.dismiss };
}

/**
 * Push toasts from anywhere under `<ToastProvider>`:
 *
 *   const toast = useToast();
 *   toast.success("Note saved");
 *   toast.error("Couldn't save note", { description: err.message });
 */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within <ToastProvider>");
  }
  return ctx.api;
}
